import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Camera, Clock, IdCard, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  pending: [Clock, "Under review", "Our team is reviewing your documents. This usually takes less than 24 hours.", "text-amber-300"],
  verified: [BadgeCheck, "You're verified", "Your ID verified badge is now visible on your profile.", "text-red-500"],
};

const Drop = ({ icon: Icon, label, file, onFile, id }) => (
  <label data-testid={`${id}-dropzone`} className="card-lux flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-3 text-center hover:border-red-600/50">
    {file ? <img src={URL.createObjectURL(file)} alt="" className="h-full w-full object-cover" /> : (<><Icon className="h-8 w-8 text-red-500" strokeWidth={1.3} /><span className="eyebrow">{label}</span></>)}
    <input data-testid={`${id}-input`} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
  </label>
);

export default function Verify() {
  const { user, refresh } = useAuth();
  const [idImg, setIdImg] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);
  const s = STATUS[user.id_status];
  const SIcon = s?.[0];
  const submit = async () => {
    const fd = new FormData();
    fd.append("id_image", idImg);
    fd.append("selfie", selfie);
    setBusy(true);
    try { await api.post("/verify", fd); toast.success("Documents submitted for review"); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <main className="mx-auto max-w-3xl px-5 py-16" data-testid="verify-page">
      <p className="eyebrow mb-3">Trust & safety · 18+</p>
      <h1 className="font-display mb-4 text-5xl">ID verification</h1>
      {s ? (
        <div data-testid={`verify-status-${user.id_status}`} className="card-lux mt-10 flex items-start gap-5 p-8">
          <SIcon className={`h-8 w-8 ${s[3]}`} /><div><h2 className="font-display text-3xl">{s[1]}</h2><p className="mt-2 text-zinc-400">{s[2]}</p></div>
        </div>
      ) : (
        <>
          {user.id_status === "rejected" && <p data-testid="verify-status-rejected" className="mb-6 flex items-center gap-2 text-red-400"><XCircle className="h-4 w-4" />Your last submission was rejected. Please try again with clearer photos.</p>}
          <p className="mb-10 text-zinc-400">Upload a government photo ID and a selfie holding it. Documents are only seen by our verification team.</p>
          <div className="grid gap-5 sm:grid-cols-2">
            <Drop id="verify-id" icon={IdCard} label="Photo ID" file={idImg} onFile={setIdImg} />
            <Drop id="verify-selfie" icon={Camera} label="Selfie with ID" file={selfie} onFile={setSelfie} />
          </div>
          <button data-testid="verify-submit-btn" disabled={!idImg || !selfie || busy} onClick={submit} className="btn-red mt-8 w-full rounded-full py-3.5 font-semibold disabled:opacity-40">
            {busy ? "Uploading…" : "Submit for review"}
          </button>
        </>
      )}
    </main>
  );
}
